import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "../lib/api";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Save, Plus, Trash2, Coins, FileText, X } from "lucide-react";
import { toast } from "sonner";

const TIER_COLOR = {
  GRATIS: "border-stone-300 bg-stone-50",
  BRONZE: "border-orange-300 bg-orange-50",
  PRATA:  "border-slate-300 bg-slate-50",
  OURO:   "border-amber-400 bg-amber-50",
  PLATINUM: "border-indigo-400 bg-indigo-50",
};

const PROTECTED_TIERS = new Set(["GRATIS", "BRONZE", "PRATA", "OURO", "PLATINUM"]);

const EMPTY_PLAN = {
  tier: "",
  display_name: "",
  price: 0,
  duration_days: 30,
  cashback: false,
  unlimited: false,
  description: "",
  features: [],
  signup_bonus: 0,
  active: true,
  contract_terms: "",
  voucher_enabled: false,
  search_priority: 5,
};

export default function AdminPlans() {
  const [plans, setPlans] = useState([]);
  const [drafts, setDrafts] = useState({});
  const [busy, setBusy] = useState(null);
  const [creating, setCreating] = useState(null); // form object when creating
  const [contractEditing, setContractEditing] = useState(null); // tier whose contract is being edited

  const refresh = () => {
    api.get("/admin/plans").then((r) => {
      setPlans(r.data);
      const d = {};
      r.data.forEach((p) => { d[p.tier] = { ...p, features: (p.features || []).join("\n") }; });
      setDrafts(d);
    });
  };

  useEffect(() => { refresh(); }, []);

  const update = (tier, patch) => setDrafts({ ...drafts, [tier]: { ...drafts[tier], ...patch } });

  const buildPayload = (d, tier) => ({
    tier,
    display_name: d.display_name,
    price: parseFloat(d.price),
    duration_days: parseInt(d.duration_days),
    cashback: !!d.cashback,
    unlimited: !!d.unlimited,
    description: d.description || "",
    signup_bonus: parseFloat(d.signup_bonus || 0),
    features: (typeof d.features === "string" ? d.features.split("\n") : (d.features || [])).map((s) => s.trim()).filter(Boolean),
    active: d.active !== false,
    contract_terms: d.contract_terms || "",
    voucher_enabled: !!d.voucher_enabled,
    search_priority: parseInt(d.search_priority ?? 99),
  });

  const save = async (tier) => {
    setBusy(tier);
    try {
      await api.patch(`/admin/plans/${tier}`, buildPayload(drafts[tier], tier));
      toast.success(`Plano ${tier} atualizado`);
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  const remove = async (tier) => {
    if (!window.confirm(`Excluir definitivamente o plano ${tier}? Esta ação não pode ser desfeita.`)) return;
    try {
      await api.delete(`/admin/plans/${tier}`);
      toast.success("Plano excluído");
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const createPlan = async () => {
    const tier = (creating.tier || "").trim().toUpperCase();
    if (!tier || !creating.display_name) { toast.error("Preencha tier e nome"); return; }
    try {
      await api.post("/admin/plans", buildPayload(creating, tier));
      toast.success(`Plano ${tier} criado`);
      setCreating(null);
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erro ao criar plano"); }
  };

  const saveContract = async (tier) => {
    setBusy(tier);
    try {
      await api.patch(`/admin/plans/${tier}`, buildPayload(drafts[tier], tier));
      toast.success("Termo de contrato salvo");
      setContractEditing(null);
      refresh();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(null); }
  };

  return (
    <div className="max-w-6xl mx-auto px-6 py-12">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold text-stone-900">Planos de Assinatura</h1>
          <p className="text-stone-500 text-sm mt-1">Crie novos planos, inative, ajuste preços e o conteúdo do termo de contrato.</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => setCreating({ ...EMPTY_PLAN })} className="bg-blue-900 hover:bg-blue-950 text-white rounded-xl" data-testid="plan-new">
            <Plus size={14} className="mr-1.5"/>Novo plano
          </Button>
          <a href="/admin" className="text-sm text-blue-900 hover:text-blue-950 inline-flex items-center px-2">← Voltar</a>
        </div>
      </div>

      <Card className="p-4 mb-6 rounded-2xl border-blue-200 bg-blue-50 flex items-start gap-3">
        <Coins className="text-blue-900 shrink-0 mt-0.5" size={20}/>
        <p className="text-sm text-blue-950">
          <strong>Tiers padrão</strong> (GRATIS, BRONZE, PRATA, OURO, PLATINUM) podem ser editados e <strong>inativados</strong> (campo "Ativo"), mas não excluídos.
          Planos personalizados só podem ser excluídos se nenhuma empresa estiver vinculada.
        </p>
      </Card>

      <div className="space-y-5">
        {Object.values(drafts).sort((a, b) => (a.search_priority ?? 99) - (b.search_priority ?? 99)).map((p) => {
          const isProtected = PROTECTED_TIERS.has(p.tier);
          return (
            <Card key={p.tier} className={`p-6 rounded-2xl border-2 ${TIER_COLOR[p.tier] || "border-stone-200"} ${!p.active ? "opacity-60" : ""}`} data-testid={`plan-edit-${p.tier}`}>
              <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                <div className="flex items-center gap-3">
                  <span className="font-display text-2xl font-bold text-stone-900">{p.tier}</span>
                  <span className="text-xs uppercase tracking-wider text-stone-500">tier interno</span>
                  {!p.active && <span className="px-2 py-0.5 rounded-md bg-red-100 text-red-700 text-[10px] font-bold uppercase">Inativo</span>}
                </div>
                <div className="flex items-center gap-2">
                  {p.cashback && <span className="px-2 py-0.5 rounded-md text-[10px] font-semibold bg-emerald-100 text-emerald-800">Cashback</span>}
                  {p.voucher_enabled && <span className="px-2 py-0.5 rounded-md text-[10px] font-semibold bg-blue-100 text-blue-800">Voucher</span>}
                  {p.unlimited && <span className="px-2 py-0.5 rounded-md text-[10px] font-semibold bg-amber-100 text-amber-900">Ilimitado</span>}
                  {(p.signup_bonus || 0) > 0 && <span className="px-2 py-0.5 rounded-md text-[10px] font-semibold bg-emerald-100 text-emerald-800">+R$ {parseFloat(p.signup_bonus || 0).toFixed(2)} bônus</span>}
                </div>
              </div>

              <div className="grid sm:grid-cols-4 gap-4">
                <div><Label>Nome exibido</Label><Input value={p.display_name} onChange={(e) => update(p.tier, { display_name: e.target.value })} className="rounded-xl mt-1" data-testid={`pf-name-${p.tier}`}/></div>
                <div><Label>Valor (R$)</Label><Input type="number" step="0.01" value={p.price} onChange={(e) => update(p.tier, { price: e.target.value })} className="rounded-xl mt-1" data-testid={`pf-price-${p.tier}`}/></div>
                <div><Label>Duração (dias)</Label><Input type="number" value={p.duration_days} onChange={(e) => update(p.tier, { duration_days: e.target.value })} className="rounded-xl mt-1" data-testid={`pf-days-${p.tier}`}/></div>
                <div><Label>Bônus inicial (R$)</Label><Input type="number" step="0.01" value={p.signup_bonus ?? 0} onChange={(e) => update(p.tier, { signup_bonus: e.target.value })} className="rounded-xl mt-1" data-testid={`pf-bonus-${p.tier}`}/></div>
                <div><Label>Prioridade busca</Label><Input type="number" value={p.search_priority ?? 99} onChange={(e) => update(p.tier, { search_priority: e.target.value })} className="rounded-xl mt-1 font-mono" data-testid={`pf-prio-${p.tier}`} placeholder="0=topo"/></div>
                <div className="sm:col-span-3 flex flex-wrap gap-x-5 gap-y-1 items-end">
                  <label className="text-xs flex items-center gap-2"><Switch checked={p.active !== false} onCheckedChange={(v) => update(p.tier, { active: v })} data-testid={`pf-active-${p.tier}`}/> Ativo</label>
                  <label className="text-xs flex items-center gap-2"><Switch checked={!!p.cashback} onCheckedChange={(v) => update(p.tier, { cashback: v })}/> Cashback</label>
                  <label className="text-xs flex items-center gap-2"><Switch checked={!!p.voucher_enabled} onCheckedChange={(v) => update(p.tier, { voucher_enabled: v })} data-testid={`pf-voucher-${p.tier}`}/> Voucher</label>
                  <label className="text-xs flex items-center gap-2"><Switch checked={!!p.unlimited} onCheckedChange={(v) => update(p.tier, { unlimited: v })}/> Vigência ilimitada</label>
                </div>
                <div className="sm:col-span-4"><Label>Descrição curta</Label><Input value={p.description || ""} onChange={(e) => update(p.tier, { description: e.target.value })} className="rounded-xl mt-1"/></div>
                <div className="sm:col-span-4"><Label>Features (uma por linha)</Label><Textarea value={p.features || ""} onChange={(e) => update(p.tier, { features: e.target.value })} className="rounded-xl mt-1 font-mono text-sm" rows={4}/></div>
              </div>

              <div className="flex justify-between mt-4 flex-wrap gap-2">
                <div className="flex gap-2">
                  <Button onClick={() => setContractEditing(p.tier)} variant="outline" className="rounded-xl" data-testid={`pf-contract-${p.tier}`}>
                    <FileText size={14} className="mr-1.5"/>Termo de contrato {(p.contract_terms || "").length > 0 ? `(${(p.contract_terms || "").length} caracteres)` : "(vazio)"}
                  </Button>
                  {!isProtected && (
                    <Button onClick={() => remove(p.tier)} variant="ghost" className="text-red-600 hover:bg-red-50 rounded-xl" data-testid={`pf-delete-${p.tier}`}>
                      <Trash2 size={14} className="mr-1.5"/>Excluir
                    </Button>
                  )}
                </div>
                <Button onClick={() => save(p.tier)} disabled={busy === p.tier} className="bg-blue-900 hover:bg-blue-950 text-white rounded-xl" data-testid={`pf-save-${p.tier}`}>
                  <Save size={14} className="mr-1.5"/> {busy === p.tier ? "Salvando..." : "Salvar plano"}
                </Button>
              </div>
            </Card>
          );
        })}
      </div>

      {/* CREATE PLAN DIALOG */}
      {creating && (
        <Dialog open onOpenChange={(v) => !v && setCreating(null)}>
          <DialogContent className="max-w-2xl rounded-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display text-2xl">Novo plano de assinatura</DialogTitle></DialogHeader>
            <div className="grid sm:grid-cols-2 gap-4">
              <div><Label>Tier (código interno, maiúsculas)</Label><Input value={creating.tier} onChange={(e) => setCreating({...creating, tier: e.target.value.toUpperCase()})} placeholder="DIAMANTE" className="rounded-xl mt-1 font-mono" data-testid="new-plan-tier"/></div>
              <div><Label>Nome exibido</Label><Input value={creating.display_name} onChange={(e) => setCreating({...creating, display_name: e.target.value})} className="rounded-xl mt-1" data-testid="new-plan-name"/></div>
              <div><Label>Valor (R$)</Label><Input type="number" step="0.01" value={creating.price} onChange={(e) => setCreating({...creating, price: e.target.value})} className="rounded-xl mt-1" data-testid="new-plan-price"/></div>
              <div><Label>Duração (dias)</Label><Input type="number" value={creating.duration_days} onChange={(e) => setCreating({...creating, duration_days: e.target.value})} className="rounded-xl mt-1"/></div>
              <div><Label>Bônus inicial (R$)</Label><Input type="number" step="0.01" value={creating.signup_bonus} onChange={(e) => setCreating({...creating, signup_bonus: e.target.value})} className="rounded-xl mt-1"/></div>
              <div><Label>Prioridade busca (0=topo)</Label><Input type="number" value={creating.search_priority} onChange={(e) => setCreating({...creating, search_priority: e.target.value})} className="rounded-xl mt-1"/></div>
              <div className="sm:col-span-2 flex flex-wrap gap-x-5 gap-y-1">
                <label className="text-xs flex items-center gap-2"><Switch checked={creating.cashback} onCheckedChange={(v) => setCreating({...creating, cashback: v})}/> Cashback</label>
                <label className="text-xs flex items-center gap-2"><Switch checked={creating.voucher_enabled} onCheckedChange={(v) => setCreating({...creating, voucher_enabled: v})}/> Voucher</label>
                <label className="text-xs flex items-center gap-2"><Switch checked={creating.unlimited} onCheckedChange={(v) => setCreating({...creating, unlimited: v})}/> Ilimitado</label>
                <label className="text-xs flex items-center gap-2"><Switch checked={creating.active} onCheckedChange={(v) => setCreating({...creating, active: v})}/> Ativo</label>
              </div>
              <div className="sm:col-span-2"><Label>Descrição curta</Label><Input value={creating.description} onChange={(e) => setCreating({...creating, description: e.target.value})} className="rounded-xl mt-1"/></div>
              <div className="sm:col-span-2"><Label>Features (uma por linha)</Label><Textarea value={Array.isArray(creating.features) ? creating.features.join("\n") : creating.features} onChange={(e) => setCreating({...creating, features: e.target.value})} className="rounded-xl mt-1 font-mono text-sm" rows={4}/></div>
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setCreating(null)} className="rounded-xl"><X size={14} className="mr-1"/>Cancelar</Button>
              <Button onClick={createPlan} className="bg-blue-900 hover:bg-blue-950 text-white rounded-xl" data-testid="new-plan-save"><Save size={14} className="mr-1"/>Criar plano</Button>
            </div>
          </DialogContent>
        </Dialog>
      )}

      {/* CONTRACT TERMS DIALOG */}
      {contractEditing && drafts[contractEditing] && (
        <Dialog open onOpenChange={(v) => !v && setContractEditing(null)}>
          <DialogContent className="max-w-4xl rounded-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle className="font-display text-2xl flex items-center gap-2"><FileText size={20}/> Termo de contrato — {contractEditing}</DialogTitle></DialogHeader>
            <p className="text-xs text-stone-500 mb-2">Este conteúdo será enviado por email à empresa após a assinatura do plano. Suporta texto puro ou Markdown.</p>
            <Textarea
              value={drafts[contractEditing].contract_terms || ""}
              onChange={(e) => update(contractEditing, { contract_terms: e.target.value })}
              rows={20}
              placeholder="CONTRATO DE PRESTAÇÃO DE SERVIÇOS&#10;&#10;Pelo presente instrumento, de um lado MorarBem [...]&#10;e de outro a EMPRESA contratante [...]&#10;&#10;CLÁUSULA 1ª - OBJETO&#10;..."
              className="rounded-xl font-mono text-xs"
              data-testid="plan-contract-textarea"
            />
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="outline" onClick={() => setContractEditing(null)} className="rounded-xl">Cancelar</Button>
              <Button onClick={() => saveContract(contractEditing)} disabled={busy === contractEditing} className="bg-blue-900 hover:bg-blue-950 text-white rounded-xl" data-testid="plan-contract-save">
                <Save size={14} className="mr-1.5"/>{busy === contractEditing ? "Salvando..." : "Salvar termo"}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
